/**
 * EventEmitter utility for component communication
 * Zero dependencies - lightweight implementation
 */

export type EventHandler<T = any> = (event: T) => void;

export interface EventSubscription {
  unsubscribe(): void;
}

export class EventEmitter<TEventMap extends Record<string, any> = Record<string, any>> {
  private listeners = new Map<string, Set<EventHandler>>();

  /**
   * Subscribe to event
   */
  on<K extends keyof TEventMap>(eventName: K, handler: EventHandler<TEventMap[K]>): EventSubscription {
    if (!this.listeners.has(String(eventName))) {
      this.listeners.set(String(eventName), new Set());
    }

    const handlers = this.listeners.get(String(eventName))!;
    handlers.add(handler as EventHandler);

    return {
      unsubscribe: () => {
        this.off(eventName, handler);
      }
    };
  }

  /**
   * Unsubscribe from event
   */
  off<K extends keyof TEventMap>(eventName: K, handler: EventHandler<TEventMap[K]>): void {
    const handlers = this.listeners.get(String(eventName));
    if (handlers) {
      handlers.delete(handler as EventHandler);
      
      if (handlers.size === 0) {
        this.listeners.delete(String(eventName));
      }
    }
  }

  /**
   * Emit event to all subscribers
   */
  emit<K extends keyof TEventMap>(eventName: K, event: TEventMap[K]): void {
    const handlers = this.listeners.get(String(eventName));
    if (handlers) {
      // Create a copy to avoid issues if handlers unsubscribe during emission
      [...handlers].forEach(handler => {
        try {
          handler(event);
        } catch (error) {
          console.error(`Error in event handler for ${String(eventName)}:`, error);
        }
      });
    }
  }

  /**
   * Subscribe to event once
   */
  once<K extends keyof TEventMap>(eventName: K, handler: EventHandler<TEventMap[K]>): EventSubscription {
    const onceHandler = (event: TEventMap[K]) => {
      handler(event);
      this.off(eventName, onceHandler);
    };
    
    return this.on(eventName, onceHandler);
  }

  /**
   * Remove all listeners or listeners for specific event
   */
  removeAllListeners(eventName?: keyof TEventMap): void {
    if (eventName) {
      this.listeners.delete(String(eventName));
    } else {
      this.listeners.clear();
    }
  }

  /**
   * Get number of listeners for event
   */
  listenerCount(eventName: keyof TEventMap): number {
    const handlers = this.listeners.get(String(eventName));
    return handlers ? handlers.size : 0;
  }
}

// Global event emitter for cross-component communication
export const globalEventEmitter = new EventEmitter();
